/**
 * this carousel is used in
 * 1. for-you page products tab
 * 2. product-category page
 */
import PropTypes from 'prop-types';

// Material-UI
import { Container } from '@mui/material';
import Typography from '@mui/material/Typography';

// Hooks
import useAuth from 'hooks/useAuth';
import useResponsive from 'hooks/useResponsive';
import { useLocalStorageQueue, LOCAL_STORAGE_KEYS } from 'hooks/useLocalStorageQueue';

// Components
import { useEffect, useState } from 'react';
import { CarouselDots } from 'components/carousel';
import { getMultipleBrandsByIds } from 'api/search';
import BrandCardGlideCarousel from 'components/Carousels/core/BrandCardGlideCarousel';
import ProductCardGlideCarousel from 'components/Carousels/core/ProductCardGlideCarousel/ProductCardGlideCarousel';

const settings = {
  arrows: false,
  lazyLoad: false,
  swipeToSlide: true,
  slidesToShow: 4,
  slidesToScroll: 4,
  centerMode: false,
  centerPadding: '0px',
  dots: true,
  infinite: true,
  responsive: [
    {
      breakpoint: 1281,
      settings: { slidesToShow: 4, slidesToScroll: 4 },
    },
    {
      breakpoint: 1160,
      settings: { slidesToShow: 3, slidesToScroll: 3 },
    },
    {
      breakpoint: 959,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        dots: true,
        ...CarouselDots({
          sx: { mt: 3, margin: '0px' },
        }),
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        dots: true,
        ...CarouselDots({
          sx: { mt: 3, margin: '0px' },
        }),
      },
    },
  ],
};

const Title = ({ title }) => {
  return (
    <Typography variant="h6" fontWeight="700">
      {title}
    </Typography>
  );
};

Title.propTypes = {
  title: PropTypes.string.isRequired,
};

// New hook to fetch brands by Virgo IDs
const useRecentlyViewedBrandsList = () => {
  const auth = useAuth();
  const { queue } = useLocalStorageQueue(LOCAL_STORAGE_KEYS.BRANDS);
  const ids = queue?.map((item) => item?.id);

  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const shippingCountryCode = auth?.user?.shippingCountry;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (ids.length > 0) {
          const response = await getMultipleBrandsByIds(ids, { shippingCountryCode }); // Assuming a similar API function for brands
          // Sort the response based on the order of ids in the queue
          const sortedData = [];
          ids.forEach((id) => {
            const matchingBrand = response.brands.find((brand) => brand.id === id);
            if (matchingBrand) {
              sortedData.push(matchingBrand);
            }
          });

          setData({ ...response, brands: sortedData });
        }
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to ensure it runs only on the first render

  return {
    data,
    error,
    isLoading,
  };
};

const recentBrowsedBrandsCarouselDefaultPropSx = {};

const RecentBrowsedBrandsCarousel = ({
  type = 'brands',
  carouselSettings = settings,
  viewAllRef = 'viewAllButton',
  sx = recentBrowsedBrandsCarouselDefaultPropSx,
}) => {
  const isMobile = useResponsive('down', 'sm');
  const { data, isLoading } = useRecentlyViewedBrandsList();
  const { brands, products } = data;

  if (type === 'products' && !products?.length) return null;
  if (type === 'brands' && (!brands || brands?.length < 4)) return null;

  const carousel =
    type === 'products' ? (
      <ProductCardGlideCarousel
        sx={{ mt: 3, ...sx }}
        products={products}
        isLoading={isLoading}
        title={<Title title="Products of Recently Viewed Brands" />}
        viewAllUrl={`/recent-brands?tab=products&ref=${viewAllRef}`}
        carouselSettings={carouselSettings}
      />
    ) : (
      <BrandCardGlideCarousel
        sx={{ mt: 3, ...sx }}
        brands={brands}
        isLoading={isLoading}
        title={<Title title="Recently Viewed Brands" />}
        viewMoreUrl={`/recent-brands?ref=${viewAllRef}`}
        carouselSettings={carouselSettings}
      />
    );

  return (
    <Container maxWidth="xl" disableGutters={isMobile} sx={{ mb: 3 }}>
      {carousel}
    </Container>
  );
};

export default RecentBrowsedBrandsCarousel;

RecentBrowsedBrandsCarousel.propTypes = {
  type: PropTypes.string,
  sx: PropTypes.shape({}),
  carouselSettings: PropTypes.shape({}),
  viewAllRef: PropTypes.string,
};
